import React, { useState, useRef } from 'react';

interface PortfolioImage {
  id: number;
  professional_id: number;
  image_url?: string;
  thumbnail_url?: string;
  title: string;
  description?: string;
  category: string;
  subcategory?: string;
  is_featured: boolean;
  is_before_after: boolean;
  before_image_url?: string;
  after_image_url?: string;
  tags?: string;
  created_at: string;
  updated_at: string;
}

interface AddImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageAdded: (images: PortfolioImage[]) => void;
  professionalId: number;
  availableCategories: string[];
  professionalType: 'salud' | 'belleza';
  currentPhotoCount: number; // ✅ NUEVO: Contador actual de fotos
}

interface EditImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageUpdated: (image: PortfolioImage) => void;
  image: PortfolioImage | null;
  professionalId: number;
  availableCategories: string[];
  professionalType: 'salud' | 'belleza';
}

interface DeleteImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImageDeleted: (imageId: number) => void;
  image: PortfolioImage | null;
  professionalId: number;
}


// ===================================================================
// FIX para PortfolioModals.tsx - Reemplazar localhost por API_URL
// ===================================================================
const API_URL = process.env.REACT_APP_API_URL || 'https://consultar-api.onrender.com';

// Función para manejar URLs de imágenes
const getImageUrl = (url: string | undefined) => {
  if (!url) return '';
  if (url.startsWith('http')) return url; // Ya es URL completa (Cloudinary)
  return `${API_URL}${url}`; // URL relativa (servidor local)
};

// Componente para preview de archivos
const FilePreview: React.FC<{
  file: File;
  onRemove: () => void;
}> = ({ file, onRemove }) => {
  const [previewUrl, setPreviewUrl] = useState<string>('');

  React.useEffect(() => {
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="relative group">
      <img
        src={previewUrl}
        alt={file.name}
        className="w-full h-32 object-cover rounded-lg"
      />
      <button
        onClick={onRemove}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
      >
        ×
      </button>
      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-lg">
        {file.name} ({(file.size / 1024 / 1024).toFixed(1)}MB)
      </div>
    </div>
  );
};

// Modal para agregar múltiples imágenes con carga de archivos
export const AddImageModal: React.FC<AddImageModalProps> = ({
  isOpen,
  onClose,
  onImageAdded,
  professionalId,
  availableCategories,
  professionalType,
  currentPhotoCount // ✅ NUEVO
}) => {
  const MAX_PHOTOS = 5; // ✅ LÍMITE MÁXIMO DE FOTOS
  const [files, setFiles] = useState<File[]>([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    is_featured: false,
    is_before_after: false,
    tags: ''
  });
  const [beforeAfterFiles, setBeforeAfterFiles] = useState<{
    before: File | null;
    after: File | null;
  }>({ before: null, after: null });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadMode, setUploadMode] = useState<'single' | 'multiple' | 'before-after'>('single');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const beforeInputRef = useRef<HTMLInputElement>(null);
  const afterInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    // ✅ NUEVO: Validar límite total de fotos en el portfolio
    const photosRemaining = MAX_PHOTOS - currentPhotoCount;
    if (photosRemaining <= 0) {
      setError(`Ya alcanzaste el límite de ${MAX_PHOTOS} fotos en tu portfolio. Eliminá alguna foto para subir una nueva.`);
      return;
    }
    
    // Validar tipo de archivo
    const validFiles = selectedFiles.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== selectedFiles.length) {
      setError('Solo se permiten archivos de imagen');
      return;
    }
    
    // Validar tamaño (10MB por archivo)
    const oversizedFiles = validFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Los archivos deben ser menores a 10MB');
      return;
    }
    
    // ✅ MODIFICADO: Validar que no exceda el límite total del portfolio
    if (files.length + validFiles.length > photosRemaining) {
      setError(`Solo podés subir ${photosRemaining} foto(s) más. Límite total: ${MAX_PHOTOS} fotos`);
      return;
    }
    
    // Limitar a 5 archivos por subida
    if (files.length + validFiles.length > 5) {
      setError('Máximo 5 archivos por subida');
      return;
    }
    
    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const handleBeforeAfterSelect = (type: 'before' | 'after', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Solo se permiten archivos de imagen');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo debe ser menor a 10MB');
      return;
    }

    setBeforeAfterFiles(prev => ({
      ...prev,
      [type]: file
    }));
    setError(null);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeBeforeAfterFile = (type: 'before' | 'after') => {
    setBeforeAfterFiles(prev => ({
      ...prev,
      [type]: null
    }));
  };

  const resetForm = () => {
    setFiles([]);
    setBeforeAfterFiles({ before: null, after: null });
    setFormData({
      title: '',
      description: '',
      category: '',
      subcategory: '',
      is_featured: false,
      is_before_after: false,
      tags: ''
    });
    setUploadMode('single');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // ===================================================================
      // FIX AUTENTICACIÓN en PortfolioModals.tsx
      // ===================================================================
      // ✅ OBTENER TOKEN
      let token = localStorage.getItem('token');
      
      if (!token) {
        const possibleKeys = ['consultAR_user', 'user', 'currentUser', 'authUser'];
        for (const key of possibleKeys) {
          const userData = localStorage.getItem(key);
          if (userData) {
            try {
              const parsed = JSON.parse(userData);
              if (parsed && parsed.token) {
                token = parsed.token;
                break;
              } else if (parsed && parsed.id) {
                token = String(parsed.id); // Usar ID como token si no hay token
                break;
              }
            } catch (e) {}
          }
        }
      }
      
      if (!token) {
        throw new Error('No se encontró token de autenticación. Por favor inicia sesión nuevamente.');
      }

      // Validaciones
      if (!formData.title.trim()) {
        throw new Error('El título es obligatorio');
      }
      if (!formData.category) {
        throw new Error('La categoría es obligatoria');
      }

      const formDataToSend = new FormData();

      if (uploadMode === 'before-after') {
        // Subir imagen antes/después
        if (!beforeAfterFiles.before || !beforeAfterFiles.after) {
          throw new Error('Se requieren ambas imágenes: antes y después');
        }

        formDataToSend.append('before_image', beforeAfterFiles.before);
        formDataToSend.append('after_image', beforeAfterFiles.after);
        formDataToSend.append('title', formData.title);
        formDataToSend.append('description', formData.description);
        formDataToSend.append('category', formData.category);
        formDataToSend.append('subcategory', formData.subcategory);
        formDataToSend.append('is_featured', formData.is_featured.toString());
        formDataToSend.append('tags', formData.tags);

        const response = await fetch(`${API_URL}/api/portfolio/professional/${professionalId}/upload-before-after`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });

        // ✅ VERIFICACIÓN DETALLADA DE LA RESPUESTA
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Upload before-after result:', result);
        
        if (result.success && result.image) {
          // ✅ CERRAR MODAL PRIMERO
          onClose();
          
          // ✅ NOTIFICAR AL COMPONENTE PADRE
          if (onImageAdded && typeof onImageAdded === 'function') {
            onImageAdded([result.image]);
          }
          
          // ✅ RESETEAR FORMULARIO CON DELAY
          setTimeout(() => {
            resetForm();
          }, 100);
        } else {
          throw new Error(result.message || 'Error en la respuesta del servidor');
        }

      } else {
        // Subir imágenes múltiples
        if (files.length === 0) {
          throw new Error('Selecciona al menos una imagen');
        }

        // Líneas 300-313 - Reemplazar por:

// Modo SINGLE: solo un archivo
if (files.length === 1) {
  formDataToSend.append('image', files[0]);  // ← Singular
  formDataToSend.append('title', formData.title);
  formDataToSend.append('description', formData.description);
  formDataToSend.append('category', formData.category);
  formDataToSend.append('subcategory', formData.subcategory || '');
  formDataToSend.append('is_featured', formData.is_featured.toString());
  formDataToSend.append('tags', formData.tags || '');
} else {
  // Múltiples imágenes - no soportado por ahora
  throw new Error('Por favor sube solo una imagen a la vez');
}

        const response = await fetch(`${API_URL}/api/portfolio/professional/${professionalId}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formDataToSend
        });

        // ✅ VERIFICACIÓN DETALLADA DE LA RESPUESTA
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        console.log('Upload result:', result);
        
        if (result.success && result.image) {  // ← Cambiar a singular
  // ✅ CERRAR MODAL PRIMERO
  onClose();
  
  // ✅ NOTIFICAR AL COMPONENTE PADRE
  if (onImageAdded && typeof onImageAdded === 'function') {
    onImageAdded([result.image]);  // ← Envolver en array
  }
  
  // ✅ RESETEAR FORMULARIO CON DELAY
  setTimeout(() => {
    resetForm();
  }, 100);

        } else {
          throw new Error(result.message || 'Error en la respuesta del servidor');
        }
      }

    } catch (err: any) {
      console.error('Error uploading images:', err);
      setError(err.message || 'Error al subir las imágenes');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {professionalType === 'belleza' ? '💄 Agregar Trabajo' : '🏥 Agregar Caso'}
              </h2>
              {/* ✅ NUEVO: Contador de fotos */}
              <p className="text-sm text-gray-600 mt-1">
                📸 Fotos en tu portfolio: <span className="font-semibold">{currentPhotoCount}/{MAX_PHOTOS}</span>
                {currentPhotoCount >= MAX_PHOTOS && (
                  <span className="text-red-600 ml-2">⚠️ Límite alcanzado</span>
                )}
              </p>
            </div>
            <button
              onClick={() => {
                console.log('Closing modal manually');
                resetForm();
                onClose();
              }}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Selector de modo de subida */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de contenido
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setUploadMode('single')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  uploadMode === 'single'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <span className="block text-2xl mb-1">📸</span>
                  <span className="text-sm font-medium">Imagen individual</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('multiple')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  uploadMode === 'multiple'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <span className="block text-2xl mb-1">🖼️</span>
                  <span className="text-sm font-medium">Múltiples imágenes</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setUploadMode('before-after')}
                className={`p-3 rounded-lg border-2 transition-colors ${
                  uploadMode === 'before-after'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-center">
                  <span className="block text-2xl mb-1">🔄</span>
                  <span className="text-sm font-medium">Antes/Después</span>
                </div>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Sección de archivos */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {uploadMode === 'before-after' ? 'Imágenes Antes/Después' : 'Seleccionar Archivos'}
              </h3>
              
              {uploadMode === 'before-after' ? (
                <div className="space-y-4">
                  {/* Imagen Antes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imagen "Antes" *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleBeforeAfterSelect('before', e)}
                      ref={beforeInputRef}
                      className="hidden"
                    />
                    {beforeAfterFiles.before ? (
                      <FilePreview
                        file={beforeAfterFiles.before}
                        onRemove={() => removeBeforeAfterFile('before')}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => beforeInputRef.current?.click()}
                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors"
                      >
                        <div className="text-center">
                          <span className="block text-2xl mb-2">📸</span>
                          <span className="text-sm text-gray-600">Seleccionar imagen "antes"</span>
                        </div>
                      </button>
                    )}
                  </div>

                  {/* Imagen Después */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Imagen "Después" *
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleBeforeAfterSelect('after', e)}
                      ref={afterInputRef}
                      className="hidden"
                    />
                    {beforeAfterFiles.after ? (
                      <FilePreview
                        file={beforeAfterFiles.after}
                        onRemove={() => removeBeforeAfterFile('after')}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => afterInputRef.current?.click()}
                        className="w-full h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors"
                      >
                        <div className="text-center">
                          <span className="block text-2xl mb-2">✨</span>
                          <span className="text-sm text-gray-600">Seleccionar imagen "después"</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple={uploadMode === 'multiple'}
                    onChange={handleFileSelect}
                    ref={fileInputRef}
                    className="hidden"
                  />
                  
                  {files.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        {files.map((file, index) => (
                          <FilePreview
                            key={index}
                            file={file}
                            onRemove={() => removeFile(index)}
                          />
                        ))}
                      </div>
                      
                      {uploadMode === 'multiple' && files.length < 5 && (
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-16 border-2 border-dashed border-blue-300 rounded-lg flex items-center justify-center hover:border-blue-400 transition-colors text-blue-600"
                        >
                          + Agregar más imágenes ({files.length}/5)
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 transition-colors"
                    >
                      <div className="text-center">
                        <span className="block text-3xl mb-2">📁</span>
                        <span className="text-lg font-medium text-gray-700 block mb-1">
                          {uploadMode === 'multiple' ? 'Seleccionar múltiples imágenes' : 'Seleccionar imagen'}
                        </span>
                        <span className="text-sm text-gray-500">
                          {uploadMode === 'multiple' ? 'Máximo 5 archivos, 10MB cada uno' : 'Máximo 10MB'}
                        </span>
                      </div>
                    </button>
                  )}
                </div>
              )}
              
              {/* Información adicional */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-start">
                  <span className="text-blue-600 mr-2">💡</span>
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 mb-1">Consejos para mejores fotos</h4>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Usa buena iluminación natural siempre que sea posible</li>
                      <li>• Mantén un fondo limpio y profesional</li>
                      <li>• Las imágenes se redimensionarán automáticamente para web</li>
                      {professionalType === 'belleza' && (
                        <li>• Incluye diferentes ángulos del trabajo realizado</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Sección de información */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Información del contenido</h3>
              
              <div className="space-y-4">
                {/* Título */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={professionalType === 'belleza' ? 'Ej: Extensiones volumen ruso' : 'Ej: Caso de tratamiento exitoso'}
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={professionalType === 'belleza' 
                      ? 'Describe la técnica utilizada, productos, tiempo...' 
                      : 'Describe el procedimiento, diagnóstico, tratamiento...'
                    }
                  />
                </div>

                {/* Categoría */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Categoría *
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar categoría</option>
                    {availableCategories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Subcategoría */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subcategoría (opcional)
                  </label>
                  <input
                    type="text"
                    value={formData.subcategory}
                    onChange={(e) => handleInputChange('subcategory', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={professionalType === 'belleza' 
                      ? 'Ej: Volumen ruso, Microblading...' 
                      : 'Ej: Consulta general, Especialidad...'
                    }
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Etiquetas (separadas por comas)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={professionalType === 'belleza' 
                      ? 'volumen ruso, pestañas, extensiones' 
                      : 'tratamiento, consulta, especialidad'
                    }
                  />
                </div>

                {/* Destacado */}
                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">Marcar como destacado</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Las imágenes destacadas aparecerán primero en tu portfolio
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Resumen de archivos */}
          {(files.length > 0 || beforeAfterFiles.before || beforeAfterFiles.after) && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Resumen de archivos a subir:</h4>
              {uploadMode === 'before-after' ? (
                <div className="text-sm text-gray-600">
                  {beforeAfterFiles.before && beforeAfterFiles.after ? (
                    <span>✅ 2 imágenes listas (antes/después)</span>
                  ) : (
                    <span>⚠️ Faltan imágenes por seleccionar</span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  <span>✅ {files.length} imagen{files.length !== 1 ? 'es' : ''} seleccionada{files.length !== 1 ? 's' : ''}</span>
                  <br />
                  <span>Tamaño total: {(files.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(1)}MB</span>
                </div>
              )}
            </div>
          )}

          {/* Botones */}
          <div className="flex space-x-3 mt-8">
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (uploadMode === 'before-after' ? (!beforeAfterFiles.before || !beforeAfterFiles.after) : files.length === 0)}
              className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 px-4 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Subiendo...</span>
                </>
              ) : (
                <span>
                  {uploadMode === 'before-after' ? 'Subir Antes/Después' : `Subir ${files.length} imagen${files.length !== 1 ? 'es' : ''}`}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Modal para editar imagen existente
export const EditImageModal: React.FC<EditImageModalProps> = ({
  isOpen,
  onClose,
  onImageUpdated,
  image,
  professionalId,
  availableCategories,
  professionalType
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    subcategory: '',
    is_featured: false,
    tags: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cargar datos de la imagen cuando el modal se abre
  React.useEffect(() => {
    if (image && isOpen) {
      setFormData({
        title: image.title,
        description: image.description || '',
        category: image.category,
        subcategory: image.subcategory || '',
        is_featured: image.is_featured,
        tags: image.tags || ''
      });
      setError(null);
    }
  }, [image, isOpen]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!image) return;

    setLoading(true);
    setError(null);

    try {
      // ===================================================================
      // FIX AUTENTICACIÓN en EditImageModal
      // ===================================================================
      // ✅ OBTENER TOKEN
      let token = localStorage.getItem('token');
      
      if (!token) {
        const possibleKeys = ['consultAR_user', 'user', 'currentUser', 'authUser'];
        for (const key of possibleKeys) {
          const userData = localStorage.getItem(key);
          if (userData) {
            try {
              const parsed = JSON.parse(userData);
              if (parsed && parsed.token) {
                token = parsed.token;
                break;
              } else if (parsed && parsed.id) {
                token = String(parsed.id);
                break;
              }
            } catch (e) {}
          }
        }
      }
      
      if (!token) {
        throw new Error('No se encontró token de autenticación');
      }

      // Validaciones
      if (!formData.title.trim()) {
        throw new Error('El título es obligatorio');
      }
      if (!formData.category) {
        throw new Error('La categoría es obligatoria');
      }

      // Preparar datos...
      const updateData = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        subcategory: formData.subcategory,
        is_featured: formData.is_featured,
        tags: formData.tags
      };

      const response = await fetch(`${API_URL}/api/portfolio/professional/${professionalId}/image/${image.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      // ✅ VERIFICACIÓN DETALLADA DE LA RESPUESTA
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('Update result:', result);
      
      if (result.success && result.image) {
        // ✅ CERRAR MODAL PRIMERO
        onClose();
        
        // ✅ NOTIFICAR AL COMPONENTE PADRE
        if (onImageUpdated && typeof onImageUpdated === 'function') {
          onImageUpdated(result.image);
        }
      } else {
        throw new Error(result.message || 'Error en la respuesta del servidor');
      }

    } catch (err: any) {
      console.error('Error updating image:', err);
      setError(err.message || 'Error al actualizar la imagen');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !image) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">
              {professionalType === 'belleza' ? '💄 Editar Trabajo' : '🏥 Editar Caso'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Preview de la imagen actual */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Imagen Actual
            </label>
            {image.is_before_after && image.before_image_url && image.after_image_url ? (
              <div className="grid grid-cols-2 gap-2 h-32">
                <img
                  src={getImageUrl(image.before_image_url)}
                  alt="Antes"
                  className="w-full h-full object-cover rounded"
                />
                <img
                  src={getImageUrl(image.after_image_url)}
                  alt="Después"
                  className="w-full h-full object-cover rounded"
                />
              </div>
            ) : (
              <img
                src={getImageUrl(image.image_url)}
                alt={image.title}
                className="h-32 w-full object-cover rounded"
              />
            )}
          </div>

          <div className="space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción
              </label>
              <textarea
                rows={3}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Categoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoría *
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Seleccionar categoría</option>
                {availableCategories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            {/* Subcategoría */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subcategoría (opcional)
              </label>
              <input
                type="text"
                value={formData.subcategory}
                onChange={(e) => handleInputChange('subcategory', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiquetas (separadas por comas)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) => handleInputChange('tags', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Destacado */}
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Marcar como destacado</span>
              </label>
            </div>
          </div>

          {/* Botones */}
          <div className="flex space-x-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-blue-500 to-green-500 text-white py-3 px-4 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Actualizando...</span>
                </>
              ) : (
                <span>Actualizar Imagen</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===================================================================
// DELETE IMAGE MODAL
// ===================================================================
export const DeleteImageModal: React.FC<DeleteImageModalProps> = ({
  isOpen,
  onClose,
  onImageDeleted,
  image,
  professionalId
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  if (!isOpen || !image) return null;

  const handleDelete = async () => {
    if (!image?.id) {
      setError('No se puede eliminar: ID de imagen inválido');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // El endpoint pasa a pedir token y a verificar que el portfolio sea el
      // tuyo: sin este header, borrar una imagen deja de funcionar.
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/api/portfolio/${professionalId}/images/${image.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al eliminar la imagen');
      }

      const result = await response.json();

      if (result.success) {
        // Notificar al componente padre
        if (onImageDeleted && typeof onImageDeleted === 'function') {
          onImageDeleted(image.id);
        }
        
        // Cerrar modal
        onClose();
      } else {
        throw new Error(result.message || 'Error al eliminar la imagen');
      }
    } catch (err: any) {
      console.error('Error eliminando imagen:', err);
      setError(err.message || 'Error al eliminar la imagen. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Eliminar Imagen</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Preview de la imagen a eliminar */}
        <div className="mb-6">
          <div className="relative rounded-lg overflow-hidden">
            <img
              src={getImageUrl(image.image_url)}
              alt={image.title}
              className="w-full h-48 object-cover"
            />
            <div className="absolute inset-0 bg-red-500 bg-opacity-20"></div>
          </div>
          <div className="mt-3 text-center">
            <p className="font-semibold text-gray-900">{image.title}</p>
            {image.description && (
              <p className="text-sm text-gray-600 mt-1">{image.description}</p>
            )}
          </div>
        </div>

        {/* Advertencia */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <svg
              className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-red-800">
                ¿Estás seguro?
              </h3>
              <p className="text-sm text-red-700 mt-1">
                Esta acción no se puede deshacer. La imagen será eliminada permanentemente de tu portafolio.
              </p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Botones */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg hover:shadow-lg transition-shadow disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                <span>Eliminando...</span>
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <span>Eliminar</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};